import { DialogElement } from 'theme';

// Shopify App Proxy preserves the path after its storefront root, so this
// must match the complete upstream gateway path on api-test.sigencloud.com.
const RECOMMENDATION_PATH = '/mall/sigenmate_calculator/recommendation';
const REQUEST_TIMEOUT_MS = 10000;
const VAT_OPTION_NAME = 'Versandland und Mehrwertsteuer';
const DE_STORE_VAT_VALUES = Object.freeze({
  DE: 'Deutschland (0% MwSt.)',
  AT: 'Österreich (Inkl. MwSt)'
});

if (!window.customElements.get('savings-calculator')) {
  class SavingsCalculator extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 1;
      this.toastTimer = null;
      this.carouselFrame = null;
      this.overflowTooltipFrame = null;
      this.activeOverflowTooltipTarget = null;
      this.overflowTooltip = null;
      this.carouselObserver = null;
      this.savingsAbortController = null;
      this.recommendationAbortController = null;
      this.resolvedEquipment = [];
      this.cartSubmitting = false;
    }

    connectedCallback() {
      if (this.savingsAbortController) {
        return;
      }

      this.savingsAbortController = new AbortController();
      const signal = this.savingsAbortController.signal;

      this.form = this.querySelector('.savings-calculator-form');
      this.progress = this.querySelector('[data-progress]');
      this.progressValue = this.querySelector('[data-progress-value]');
      this.footer = this.querySelector('[data-form-footer]');
      this.backButton = this.querySelector('[data-back]');
      this.continueButton = this.querySelector('[data-continue]');
      this.toast = this.querySelector('[data-toast]');
      this.productCarousel = this.querySelector('[data-product-carousel]');
      this.carouselPrevious = this.querySelector('[data-carousel-previous]');
      this.carouselNext = this.querySelector('[data-carousel-next]');
      this.addToCartButton = this.querySelector('[data-add-setup-to-cart]');
      this.setupDetailsLink = this.querySelector('[data-setup-details]');
      this.cartStatus = this.querySelector('[data-cart-status]');
      this.recommendationErrorDescription = this.querySelector('[data-recommendation-error-description]');
      this.defaultRecommendationErrorDescription = this.recommendationErrorDescription?.textContent.trim() || '';
      this.equipmentCardTemplates = Array.from(this.querySelectorAll('.savings-calculator-result__product'))
        .map((card) => card.cloneNode(true));
      this.overflowTooltip = document.createElement('div');
      this.overflowTooltip.className = 'savings-calculator-overflow-tooltip';
      this.overflowTooltip.setAttribute('role', 'tooltip');
      this.overflowTooltip.hidden = true;
      document.body.append(this.overflowTooltip);

      this.form?.addEventListener('input', (event) => {
        if (event.target.matches('input:not([type="radio"])')) {
          event.target.dataset.touched = 'true';
          this.validateField(event.target, true);
        }

        this.updateContinueState();
      }, { signal });
      this.form?.addEventListener('change', (event) => {
        if (event.target.matches('[name="country_code"]')) {
          const postcode = this.querySelector('[name="postcode"]');
          this.syncPostcodeValidation(Boolean(postcode?.dataset.touched || postcode?.value.trim()));
        }

        if (event.target.matches('[name="type"]')) {
          this.syncConditionalFields({ resetTariffValidation: true });
        } else if (event.target.matches('[name="has_existing_solar"]')) {
          this.syncConditionalFields();
        }

        if (event.target.matches('input:not([type="radio"])')) {
          event.target.dataset.touched = 'true';
          this.validateField(event.target, true);
        }

        this.updateContinueState();
      }, { signal });
      this.form?.addEventListener('blur', (event) => {
        const nextTarget = event.relatedTarget;
        const isModeSwitch = nextTarget instanceof Element
          && nextTarget.closest('[data-consumption-mode], input[name="type"]');

        if (event.target.matches('input:not([type="radio"])') && !isModeSwitch) {
          event.target.dataset.touched = 'true';
          this.validateField(event.target, true);
        }
      }, { capture: true, signal });
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.goForward();
      }, { signal });
      this.backButton?.addEventListener('click', () => this.goBack(), { signal });
      this.continueButton?.addEventListener('click', () => this.goForward(), { signal });
      this.querySelector('[data-retry]')?.addEventListener('click', () => this.calculate(), { signal });
      this.querySelector('[data-edit]')?.addEventListener('click', () => this.setStep(1), { signal });
      this.addToCartButton?.addEventListener('click', () => this.addSetupToCart(), { signal });

      this.querySelectorAll('[data-consumption-mode]').forEach((button) => {
        button.addEventListener('click', () => {
          this.syncConsumptionFields(button.dataset.consumptionMode, true, true);
          this.updateContinueState();
        }, { signal });
      });

      this.querySelectorAll('[data-coming-soon]').forEach((button) => {
        button.addEventListener('click', () => this.showToast(this.getString('comingSoon')), { signal });
      });

      this.carouselPrevious?.addEventListener('click', () => this.moveProductCarousel(-1), { signal });
      this.carouselNext?.addEventListener('click', () => this.moveProductCarousel(1), { signal });
      this.productCarousel?.addEventListener('scroll', () => this.scheduleCarouselSync(), { passive: true, signal });
      this.productCarousel?.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
          return;
        }

        event.preventDefault();
        this.moveProductCarousel(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });
      this.addEventListener('pointerover', (event) => this.handleOverflowTooltipEnter(event), { signal });
      this.addEventListener('pointerout', (event) => this.handleOverflowTooltipLeave(event), { signal });
      this.addEventListener('focusin', (event) => this.handleOverflowTooltipFocus(event), { signal });
      this.addEventListener('focusout', (event) => this.handleOverflowTooltipBlur(event), { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.hideOverflowTooltip();
        }
      }, { signal });
      this.addEventListener('scroll', () => this.hideOverflowTooltip(), { capture: true, passive: true, signal });
      window.addEventListener('resize', () => {
        this.hideOverflowTooltip();
        this.scheduleCarouselSync();
        this.scheduleOverflowTooltipSync();
      }, { signal });
      window.addEventListener('scroll', () => this.hideOverflowTooltip(), { passive: true, signal });

      if (this.productCarousel) {
        this.carouselObserver = new MutationObserver(() => this.scheduleCarouselSync());
        this.carouselObserver.observe(this.productCarousel, { childList: true });
      }

      this.resetCalculator();
    }

    disconnectedCallback() {
      window.clearTimeout(this.toastTimer);
      window.cancelAnimationFrame(this.carouselFrame);
      window.cancelAnimationFrame(this.overflowTooltipFrame);
      this.carouselFrame = null;
      this.overflowTooltipFrame = null;
      this.overflowTooltip?.remove();
      this.overflowTooltip = null;
      this.activeOverflowTooltipTarget = null;
      this.carouselObserver?.disconnect();
      this.carouselObserver = null;
      this.recommendationAbortController?.abort();
      this.recommendationAbortController = null;
      this.savingsAbortController?.abort();
      this.savingsAbortController = null;
    }

    resetCalculator() {
      window.clearTimeout(this.toastTimer);
      this.recommendationAbortController?.abort();
      this.recommendationAbortController = null;
      this.resolvedEquipment = [];
      this.cartSubmitting = false;
      this.form?.reset();
      this.syncPostcodeValidation(false);
      this.syncConsumptionFields();
      this.syncConditionalFields();
      this.form?.querySelectorAll('input:not([type="radio"])').forEach((field) => {
        delete field.dataset.touched;
        this.validateField(field, false);
      });
      this.updateCartActions();
      this.updateSetupDetailsLink();
      this.hideCartStatus();
      this.hideToast();
      this.resetRecommendationError();
      this.setStep(1);
    }

    setStep(step) {
      this.currentStep = Math.min(5, Math.max(1, Number(step) || 1));
      delete this.dataset.currentView;

      this.querySelectorAll('[data-step]').forEach((element) => {
        element.hidden = Number(element.dataset.step) !== this.currentStep;
      });

      this.querySelectorAll('[data-view]').forEach((element) => {
        element.hidden = true;
      });

      if (this.progress) {
        this.progress.hidden = false;
        this.progress.textContent = `${this.currentStep} / 5`;
      }

      if (this.progressValue) {
        this.progressValue.parentElement.hidden = false;
        this.progressValue.style.width = `${this.currentStep * 20}%`;
      }

      if (this.footer) {
        this.footer.hidden = false;
      }

      if (this.backButton) {
        this.backButton.hidden = this.currentStep === 1;
      }

      this.updateContinueState();
      this.querySelector(`[data-step="${this.currentStep}"] h3`)?.focus({ preventScroll: true });
    }

    showView(name) {
      this.dataset.currentView = name;
      this.querySelectorAll('[data-step]').forEach((element) => {
        element.hidden = true;
      });
      this.querySelectorAll('[data-view]').forEach((element) => {
        element.hidden = element.dataset.view !== name;
      });

      if (this.progress) {
        const showCompletedProgress = this.dataset.displayMode === 'page' && name === 'loading';
        this.progress.hidden = !showCompletedProgress;

        if (showCompletedProgress) {
          this.progress.textContent = '5 / 5';
        }
      }

      if (this.progressValue) {
        const showCompletedProgress = this.dataset.displayMode === 'page' && name === 'loading';
        this.progressValue.parentElement.hidden = !showCompletedProgress;
        this.progressValue.style.width = showCompletedProgress ? '100%' : `${this.currentStep * 20}%`;
      }

      if (this.footer) {
        this.footer.hidden = true;
      }

      if (name === 'result') {
        this.scheduleCarouselSync(true);
        this.scheduleOverflowTooltipSync();

        document.fonts?.ready.then(() => {
          if (this.isConnected && this.dataset.currentView === 'result') {
            this.scheduleOverflowTooltipSync();
          }
        });
      }
    }

    scheduleCarouselSync(reset = false) {
      window.cancelAnimationFrame(this.carouselFrame);
      this.carouselFrame = window.requestAnimationFrame(() => {
        this.carouselFrame = null;
        this.syncProductCarousel(reset);
      });
    }

    getVisibleProductCards() {
      return Array.from(this.productCarousel?.querySelectorAll('.savings-calculator-result__product') || [])
        .filter((card) => !card.hidden);
    }

    syncProductCarousel(reset = false) {
      if (!this.productCarousel || !this.carouselPrevious || !this.carouselNext) {
        return;
      }

      if (reset) {
        this.productCarousel.scrollTo({ left: 0, behavior: 'auto' });
      }

      const cards = this.getVisibleProductCards();
      const hasOverflow = cards.length > 3 && this.productCarousel.scrollWidth - this.productCarousel.clientWidth > 1;
      const atStart = this.productCarousel.scrollLeft <= 1;
      const atEnd = this.productCarousel.scrollLeft + this.productCarousel.clientWidth >= this.productCarousel.scrollWidth - 1;

      this.carouselPrevious.hidden = !hasOverflow;
      this.carouselNext.hidden = !hasOverflow;
      this.carouselPrevious.disabled = !hasOverflow || atStart;
      this.carouselNext.disabled = !hasOverflow || atEnd;
    }

    moveProductCarousel(direction) {
      if (!this.productCarousel) {
        return;
      }

      const card = this.getVisibleProductCards()[0];
      if (!card) {
        return;
      }

      const styles = window.getComputedStyle(this.productCarousel);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const distance = card.getBoundingClientRect().width + gap;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

      this.productCarousel.scrollBy({ left: direction * distance, behavior });
    }

    goBack() {
      if (this.currentStep > 1) {
        this.setStep(this.currentStep - 1);
      }
    }

    goForward() {
      const currentFields = this.getCurrentFields();

      currentFields.forEach((field) => {
        field.dataset.touched = 'true';
        this.validateField(field, true);
      });

      if (!this.isCurrentStepValid()) {
        const invalidField = currentFields.find((field) => !field.checkValidity() || field.value.trim() === '');
        if (invalidField) {
          invalidField.focus({ preventScroll: true });
        }
        return;
      }

      if (this.currentStep < 5) {
        this.setStep(this.currentStep + 1);
        return;
      }

      this.calculate();
    }

    async calculate() {
      this.resetRecommendationError();
      this.showView('loading');
      this.resolvedEquipment = [];
      this.hideCartStatus();
      this.updateCartActions();
      this.updateSetupDetailsLink();

      this.recommendationAbortController?.abort();
      this.recommendationAbortController = new AbortController();
      const controller = this.recommendationAbortController;
      const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      try {
        const payload = this.buildRecommendationPayload();
        const data = await this.requestRecommendation(payload, controller.signal);
        this.resolvedEquipment = await this.resolveEquipment(
          data.equipment,
          payload.location.country_code,
          controller.signal
        );
        this.renderRecommendation(data, this.resolvedEquipment);
        this.renderResultAnswers();
        this.showView('result');
      } catch (error) {
        if (error.name !== 'AbortError' || this.recommendationAbortController === controller) {
          console.error('[SigenMate calculator] Recommendation preparation failed', error);
          if (error?.code === 'INVALID_RECOMMENDATION_RESPONSE' || error?.code === 'INCOMPLETE_RECOMMENDATION_RESULT') {
            console.error('[SigenMate calculator] Recommendation response diagnostics', {
              code: error.code,
              reason: error.reason,
              requestId: error.requestId,
              requestUrl: error.requestUrl,
              httpStatus: error.httpStatus,
              invalidFields: error.invalidFields,
              details: error.details,
              responseBody: error.responseBody,
              cause: error.cause
            });
          }
          this.renderRecommendationError(error);
          this.showView('error');
        }
      } finally {
        window.clearTimeout(timeoutId);

        if (this.recommendationAbortController === controller) {
          this.recommendationAbortController = null;
        }
      }
    }

    buildRecommendationPayload() {
      const formData = new FormData(this.form);
      const type = String(formData.get('type') || '');
      const has_existing_solar = formData.get('has_existing_solar') === 'true';
      const inputMode = String(formData.get('input_mode') || 'annual_usage');
      const consumption = { input_mode: inputMode };
      const tariff = { type };
      const solar = { has_existing_solar };

      if (inputMode === 'monthly_bill') {
        consumption.monthly_bill_eur = Number(formData.get('monthly_bill_eur'));
      } else {
        consumption.annual_usage_kwh = Number(formData.get('annual_usage_kwh'));
      }

      if (type === 'flat') {
        tariff.flat_rate_eur_per_kwh = Number(formData.get('flat_rate_eur_per_kwh'));
      } else if (type === 'day_night') {
        tariff.day_rate_eur_per_kwh = Number(formData.get('day_rate_eur_per_kwh'));
        tariff.night_rate_eur_per_kwh = Number(formData.get('night_rate_eur_per_kwh'));
      }

      if (has_existing_solar) {
        solar.existing_capacity_wp = Number(formData.get('existing_capacity_wp'));
      }

      return {
        location: {
          country_code: String(formData.get('country_code') || ''),
          postcode: String(formData.get('postcode') || '').trim()
        },
        consumption,
        tariff,
        solar,
        preference: {
          objective: String(formData.get('objective') || '')
        }
      };
    }

    async requestRecommendation(payload, signal) {
      const appProxyPath = (this.dataset.appProxyPath || '/apps/im-chat').replace(/\/+$/, '');
      const url = `${appProxyPath}${RECOMMENDATION_PATH}?source=dtc`;
      // 通过 Shopify App Proxy 请求后端推荐接口，获取推荐结果
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        signal
      });

      const responseBody = await response.text();
      let result;

      try {
        result = JSON.parse(responseBody);
      } catch (cause) {
        if (!response.ok) {
          throw new Error(`Recommendation request failed with HTTP ${response.status}`);
        }

        throw this.createRecommendationError(
          'INVALID_RECOMMENDATION_RESPONSE',
          'Recommendation API returned a response that could not be parsed as JSON',
          {
            reason: 'invalid_json',
            requestUrl: url,
            httpStatus: response.status,
            responseBody,
            cause
          }
        );
      }

      const recommendationEnvelope = result?.data;

      const algorithmError = Array.isArray(recommendationEnvelope?.errors)
        ? recommendationEnvelope.errors.find((error) => error?.message || error?.reason)
        : null;
      const errorMessage = result?.msg
        || recommendationEnvelope?.message
        || algorithmError?.message
        || algorithmError?.reason;

      if (!response.ok) {
        throw new Error(errorMessage || `Recommendation request failed with HTTP ${response.status}`);
      }

      const hasResultCodeValue = typeof result?.code === 'number'
        || (typeof result?.code === 'string' && result.code.trim() !== '');
      const hasResultCode = result
        && typeof result === 'object'
        && !Array.isArray(result)
        && Object.prototype.hasOwnProperty.call(result, 'code')
        && hasResultCodeValue
        && Number.isFinite(Number(result.code));

      if (!hasResultCode) {
        throw this.createIncompleteRecommendationError([
          {
            path: 'response',
            reason: 'invalid_result_envelope',
            value: result
          }
        ], recommendationEnvelope?.request_id);
      }

      if (Number(result.code) !== 0) {
        throw new Error(errorMessage || 'Recommendation API returned an invalid response');
      }

      const recommendationData = recommendationEnvelope?.data;
      if (!recommendationData || typeof recommendationData !== 'object' || Array.isArray(recommendationData)) {
        throw this.createIncompleteRecommendationError([
          {
            path: 'data.data',
            reason: 'missing_or_invalid_recommendation_result',
            value: recommendationData
          }
        ], recommendationEnvelope?.request_id);
      }

      const equipmentIssues = this.getEquipmentValidationIssues(recommendationData.equipment);
      if (equipmentIssues.length > 0) {
        throw this.createIncompleteRecommendationError(equipmentIssues, recommendationEnvelope?.request_id);
      }

      // 后端接口的数据
      return recommendationData;
    }

    async resolveEquipment(equipment, countryCode, signal) {
      const items = equipment.map((item) => {
        return {
          sku: item.SKU.trim(),
          handle: item.handle.trim(),
          quantity: item.quantity
        };
      });
      const productRequests = new Map();
      // 请求Product Ajax API 地址，获取商品的详细信息
      const getProduct = (handle) => {
        if (!productRequests.has(handle)) {
          const productUrl = `${window.Shopify.routes.root}products/${encodeURIComponent(handle)}.js`;
          productRequests.set(handle, (async () => {
            try {
              const response = await fetch(productUrl, {
                headers: { Accept: 'application/json' },
                signal
              });
              const product = await response.json().catch(() => null);

              if (!response.ok) {
                throw this.createRecommendationError(
                  'PRODUCT_LOAD_FAILED',
                  `Shopify product request failed for handle ${handle} with HTTP ${response.status}`,
                  {
                    handle,
                    productUrl,
                    httpStatus: response.status,
                    reason: 'http'
                  }
                );
              }

              if (!product || typeof product !== 'object' || !Array.isArray(product.variants)) {
                throw this.createRecommendationError(
                  'PRODUCT_LOAD_FAILED',
                  `Shopify product returned an invalid response for handle ${handle}`,
                  {
                    handle,
                    productUrl,
                    httpStatus: response.status,
                    reason: 'invalid_response'
                  }
                );
              }

              return product;
            } catch (error) {
              if (error?.code === 'PRODUCT_LOAD_FAILED') {
                throw error;
              }

              if (error?.name === 'AbortError') {
                const timeoutError = this.createRecommendationError(
                  'PRODUCT_LOAD_FAILED',
                  `Shopify product request timed out for handle ${handle}`,
                  {
                    handle,
                    productUrl,
                    reason: 'timeout',
                    cause: error
                  }
                );
                timeoutError.name = 'AbortError';
                throw timeoutError;
              }

              throw this.createRecommendationError(
                'PRODUCT_LOAD_FAILED',
                `Shopify product request failed for handle ${handle}`,
                {
                  handle,
                  productUrl,
                  reason: 'network',
                  cause: error
                }
              );
            }
          })());
        }

        return productRequests.get(handle);
      };

      return Promise.all(items.map(async ({ sku, handle, quantity }) => {
        const product = await getProduct(handle);
        const availableSkus = Array.from(new Set(product.variants
          .map((candidate) => typeof candidate?.sku === 'string' ? candidate.sku.trim() : '')
          .filter(Boolean)));
        const shouldMatchCountryVariant = Object.prototype.hasOwnProperty.call(DE_STORE_VAT_VALUES, countryCode);
        const variant = shouldMatchCountryVariant
          ? this.resolveDeStoreVariant(product, sku, handle, countryCode, availableSkus)
          : product.variants.find((candidate) => candidate?.sku === sku);
        const productTitle = typeof product.title === 'string' ? product.title.trim() : '';
        const variantTitle = typeof variant?.title === 'string' ? variant.title.trim() : '';
        const variantId = variant?.id;

        if (!variant) {
          throw this.createRecommendationError(
            'SKU_MISMATCH',
            `Shopify variant could not be matched for SKU ${sku} on ${handle}`,
            {
              handle,
              sku,
              availableSkus,
              reason: 'sku_mismatch'
            }
          );
        }

        if (!productTitle || !variantId) {
          throw new Error(`Shopify product data is incomplete for SKU ${sku} on ${handle}`);
        }

        const productImage = typeof product.featured_image === 'string'
          ? product.featured_image
          : product.featured_image?.src;
        const firstProductImage = Array.isArray(product.images) ? product.images[0] : '';
        const imageUrl = variant.featured_image?.src
          || productImage
          || (typeof firstProductImage === 'string' ? firstProductImage : firstProductImage?.src)
          || '';
        const tags = Array.isArray(product.tags) ? product.tags : [];

        return {
          sku,
          handle,
          quantity,
          productTitle,
          variantTitle,
          variantId,
          imageUrl,
          available: variant.available === true,
          isBundle: tags.some((tag) => typeof tag === 'string' && tag.trim().toLowerCase() === 'bundle')
        };
      }));
    }

    resolveDeStoreVariant(product, sku, handle, countryCode, availableSkus) {
      const skuCandidates = product.variants.filter((candidate) => {
        return typeof candidate?.sku === 'string' && candidate.sku.trim() === sku;
      });

      if (skuCandidates.length === 0) {
        return undefined;
      }

      const expectedOptionValue = DE_STORE_VAT_VALUES[countryCode];
      const vatOptionIndex = Array.isArray(product.options)
        ? product.options.findIndex((option) => {
          const optionName = typeof option === 'string' ? option : option?.name;
          return String(optionName || '').trim() === VAT_OPTION_NAME;
        })
        : -1;
      const matches = expectedOptionValue && vatOptionIndex >= 0
        ? skuCandidates.filter((candidate) => {
          const optionValue = Array.isArray(candidate?.options) ? candidate.options[vatOptionIndex] : '';
          return String(optionValue || '').trim() === expectedOptionValue;
        })
        : [];

      if (matches.length !== 1) {
        const availableOptionValues = vatOptionIndex >= 0
          ? Array.from(new Set(skuCandidates.map((candidate) => {
            const optionValue = Array.isArray(candidate?.options) ? candidate.options[vatOptionIndex] : '';
            return String(optionValue || '').trim();
          }).filter(Boolean)))
          : [];

        throw this.createRecommendationError(
          'COUNTRY_VARIANT_MISMATCH',
          `Shopify variant could not be matched for country ${countryCode}, SKU ${sku}, and handle ${handle}`,
          {
            handle,
            sku,
            countryCode,
            expectedOptionValue: expectedOptionValue || '',
            optionName: VAT_OPTION_NAME,
            availableOptionValues,
            availableSkus,
            matchingVariantIds: matches.map((candidate) => candidate.id),
            reason: expectedOptionValue
              ? vatOptionIndex >= 0
                ? matches.length > 1 ? 'ambiguous_country_variant' : 'country_variant_not_found'
                : 'country_option_not_found'
              : 'unsupported_country'
          }
        );
      }

      return matches[0];
    }

    createRecommendationError(code, message, details = {}) {
      const error = new Error(message);
      error.code = code;
      Object.assign(error, details);
      return error;
    }

    createIncompleteRecommendationError(details, requestId) {
      const invalidFields = Array.from(new Set(details.map(({ path }) => path)));

      return this.createRecommendationError(
        'INCOMPLETE_RECOMMENDATION_RESULT',
        'Recommendation API returned incomplete result data',
        {
          reason: 'incomplete_result',
          requestId,
          invalidFields,
          details
        }
      );
    }

    getEquipmentValidationIssues(equipment) {
      if (!Array.isArray(equipment)) {
        return [{
          path: 'data.data.equipment',
          reason: 'missing_or_invalid_equipment',
          value: equipment
        }];
      }

      if (equipment.length === 0) {
        return [{
          path: 'data.data.equipment',
          reason: 'empty_equipment',
          value: equipment
        }];
      }

      return equipment.flatMap((item, index) => {
        const issues = [];
        const handle = typeof item?.handle === 'string' ? item.handle.trim() : '';
        const sku = typeof item?.SKU === 'string' ? item.SKU.trim() : '';

        if (!handle) {
          issues.push({
            path: 'data.data.equipment[].handle',
            index,
            reason: 'missing_or_invalid_handle',
            value: item?.handle
          });
        }

        if (!sku) {
          issues.push({
            path: 'data.data.equipment[].SKU',
            index,
            reason: 'missing_or_invalid_sku',
            value: item?.SKU
          });
        }

        if (!Number.isInteger(item?.quantity) || item.quantity <= 0) {
          issues.push({
            path: 'data.data.equipment[].quantity',
            index,
            reason: 'missing_or_invalid_quantity',
            value: item?.quantity
          });
        }

        return issues;
      });
    }

    renderRecommendationError(error) {
      if (!this.recommendationErrorDescription) {
        return;
      }

      if (error?.code === 'INVALID_RECOMMENDATION_RESPONSE') {
        this.recommendationErrorDescription.textContent = this.getString('invalidRecommendationResponse');
        return;
      }

      if (error?.code === 'INCOMPLETE_RECOMMENDATION_RESULT') {
        this.recommendationErrorDescription.textContent = this.getString('incompleteRecommendationResult');
        return;
      }

      if (error?.code === 'PRODUCT_LOAD_FAILED') {
        const messageKey = {
          http: 'productLoadHttp',
          invalid_response: 'productLoadInvalid',
          network: 'productLoadNetwork',
          timeout: 'productLoadTimeout'
        }[error.reason];

        if (messageKey) {
          this.recommendationErrorDescription.textContent = this.formatString(messageKey, {
            handle: error.handle,
            status: error.httpStatus
          });
          return;
        }
      }

      if (error?.code === 'SKU_MISMATCH') {
        const availableSkus = Array.isArray(error.availableSkus) ? error.availableSkus : [];
        const messageKey = availableSkus.length > 0 ? 'skuMismatch' : 'skuMismatchNoAvailable';
        this.recommendationErrorDescription.textContent = this.formatString(messageKey, {
          sku: error.sku,
          handle: error.handle,
          availableSkus: this.formatSkuList(availableSkus)
        });
        return;
      }

      if (error?.code === 'COUNTRY_VARIANT_MISMATCH') {
        this.recommendationErrorDescription.textContent = this.formatString('countryVariantMismatch', {
          country: error.countryCode,
          sku: error.sku,
          handle: error.handle,
          expectedOption: error.expectedOptionValue
        });
        return;
      }

      this.resetRecommendationError();
    }

    resetRecommendationError() {
      if (this.recommendationErrorDescription) {
        this.recommendationErrorDescription.textContent = this.defaultRecommendationErrorDescription;
      }
    }

    formatString(key, replacements = {}) {
      return Object.entries(replacements).reduce((message, [name, value]) => {
        const token = `__${name.replace(/[A-Z]/g, (letter) => `_${letter}`).toUpperCase()}__`;
        return message.split(token).join(value == null ? '' : String(value));
      }, this.getString(key));
    }

    formatSkuList(skus) {
      const quotedSkus = skus.map((sku) => `\"${sku}\"`);

      if (typeof Intl.ListFormat === 'function') {
        return new Intl.ListFormat(document.documentElement.lang || undefined, {
          style: 'long',
          type: 'conjunction'
        }).format(quotedSkus);
      }

      return quotedSkus.join(', ');
    }

    syncConsumptionFields(mode, focusActiveField = false, resetValidation = false) {
      const inputMode = mode === 'monthly_bill' ? 'monthly_bill' : 'annual_usage';
      const inputModeField = this.querySelector('[name="input_mode"]');

      if (inputModeField) {
        inputModeField.value = inputMode;
      }

      this.querySelectorAll('[data-consumption-fields]').forEach((container) => {
        const active = container.dataset.consumptionFields === inputMode;
        container.hidden = !active;
        container.querySelectorAll('input').forEach((field) => {
          field.disabled = !active;

          if (!active || resetValidation) {
            this.resetFieldValidation(field);
          }
        });
      });

      if (focusActiveField) {
        this.querySelector(`[data-consumption-fields="${inputMode}"] input`)?.focus({ preventScroll: true });
      }
    }

    syncConditionalFields({ resetTariffValidation = false } = {}) {
      const tariffType = this.getSelected('type')?.value || 'flat';
      this.querySelectorAll('[data-tariff-fields]').forEach((container) => {
        const active = container.dataset.tariffFields === tariffType;
        container.hidden = !active;
        container.querySelectorAll('input').forEach((field) => {
          field.disabled = !active;

          if (resetTariffValidation) {
            this.resetFieldValidation(field);
          }
        });
      });

      const hasExistingSolar = this.getSelected('has_existing_solar')?.value === 'true';
      const solarFields = this.querySelector('[data-existing-solar-fields]');
      if (solarFields) {
        solarFields.hidden = !hasExistingSolar;
        solarFields.querySelectorAll('input').forEach((field) => {
          field.disabled = !hasExistingSolar;
        });
      }
    }

    resetFieldValidation(field) {
      const error = field.closest('.savings-calculator-field')?.querySelector('[data-field-error]');

      delete field.dataset.touched;
      field.setAttribute('aria-invalid', 'false');

      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
    }

    updateContinueState() {
      if (this.continueButton) {
        this.continueButton.disabled = false;
      }
    }

    isCurrentStepValid() {
      const fields = this.getCurrentFields();

      if (this.currentStep === 1) {
        return Boolean(this.getSelected('country_code')) && fields.every((field) => field.checkValidity() && field.value.trim() !== '');
      }

      if (this.currentStep === 2 || this.currentStep === 3) {
        return fields.every((field) => field.checkValidity() && Number(field.value) > 0);
      }

      if (this.currentStep === 4) {
        return Boolean(this.getSelected('has_existing_solar')) && fields.every((field) => field.checkValidity() && field.value.trim() !== '');
      }

      if (this.currentStep === 5) {
        return Boolean(this.getSelected('objective'));
      }

      return false;
    }

    getCurrentFields() {
      return Array.from(this.querySelectorAll(`[data-step="${this.currentStep}"] input:not([type="radio"]):not([type="hidden"]):not(:disabled)`));
    }

    syncPostcodeValidation(showError = false) {
      const field = this.querySelector('[name="postcode"]');
      if (!field) {
        return;
      }

      const country = this.getSelected('country_code')?.value || 'DE';
      const formats = {
        DE: { pattern: '[0-9]{5}', minLength: 5, maxLength: 5, inputMode: 'numeric', placeholder: '10115' },
        AT: { pattern: '[1-9][0-9]{3}', minLength: 4, maxLength: 4, inputMode: 'numeric', placeholder: '1010' },
        NL: { pattern: '[1-9][0-9]{3} ?[A-Za-z]{2}', minLength: 6, maxLength: 7, inputMode: 'text', placeholder: '1012 AB' }
      };
      const format = formats[country] || formats.DE;
      const countryError = field.dataset[`errorPattern${country.charAt(0)}${country.slice(1).toLowerCase()}`];

      // Clear both bounds before applying the next country. Increasing
      // minLength while the previous, shorter maxLength is still active throws
      // an IndexSizeError and leaves the remaining validation state stale.
      field.removeAttribute('minlength');
      field.removeAttribute('maxlength');
      field.setAttribute('pattern', format.pattern);
      field.setAttribute('minlength', String(format.minLength));
      field.setAttribute('maxlength', String(format.maxLength));
      field.setAttribute('inputmode', format.inputMode);
      field.setAttribute('placeholder', format.placeholder);
      field.dataset.errorPattern = countryError || field.dataset.errorPattern || '';
      this.validateField(field, showError);
    }

    validateField(field, showError = false) {
      const error = field.closest('.savings-calculator-field')?.querySelector('[data-field-error]');
      if (!error) {
        return field.checkValidity();
      }

      let message = '';
      if (showError && !field.validity.valid) {
        if (field.validity.valueMissing) {
          message = field.dataset.errorRequired || '';
        } else if (field.validity.badInput) {
          message = field.dataset.errorNumber || '';
        } else if (field.validity.rangeUnderflow || field.validity.rangeOverflow) {
          message = field.dataset.errorRange || '';
        } else if (field.validity.stepMismatch) {
          message = field.dataset.errorStep || field.dataset.errorNumber || '';
        } else if (field.validity.patternMismatch || field.validity.tooShort || field.validity.tooLong) {
          message = field.dataset.errorPattern || '';
        }
      }

      error.textContent = message;
      error.hidden = !message;
      field.setAttribute('aria-invalid', message ? 'true' : 'false');
      return field.checkValidity();
    }

    getSelected(name) {
      return this.querySelector(`input[name="${name}"]:checked`);
    }

    renderRecommendation(data, resolvedEquipment) {
      const estimates = data.estimates || {};
      const annualSavings = this.querySelector('[data-annual-savings]');
      const paybackYears = this.querySelector('[data-payback-years]');
      const solarGeneration = this.querySelector('[data-solar-generation]');
      const setupPrice = this.querySelector('[data-setup-price]');

      if (annualSavings) {
        annualSavings.textContent = this.formatCurrency(estimates.annual_savings_eur);
      }
      if (paybackYears) {
        paybackYears.textContent = `~${this.formatNumber(estimates.payback_years, 1)}`;
      }
      if (solarGeneration) {
        solarGeneration.textContent = this.formatNumber(estimates.annual_solar_generation_kwh);
      }
      if (setupPrice) {
        setupPrice.textContent = this.formatCurrency(estimates.setup_price_eur);
      }

      this.renderEquipment(resolvedEquipment);
      this.hideCartStatus();
      this.updateCartActions();
      this.updateSetupDetailsLink();
    }

    renderEquipment(resolvedEquipment) {
      const rows = Array.isArray(resolvedEquipment) ? resolvedEquipment : [];
      const equipmentList = this.querySelector('[data-equipment-list]');
      const setup = equipmentList?.closest('.savings-calculator-result__setup');
      const productList = this.querySelector('.savings-calculator-result__products');

      if (setup) {
        setup.hidden = rows.length === 0;
      }
      if (productList) {
        productList.hidden = rows.length === 0;
      }

      if (equipmentList) {
        equipmentList.replaceChildren(...rows.map(({ productTitle, variantTitle, quantity, sku }) => {
          const row = document.createElement('div');
          const term = document.createElement('dt');
          const description = document.createElement('dd');
          const specification = document.createElement('span');
          const count = document.createElement('strong');

          term.textContent = productTitle;
          specification.textContent = variantTitle && variantTitle !== 'Default Title' ? variantTitle : sku;
          term.dataset.overflowTooltip = '';
          specification.dataset.overflowTooltip = '';
          count.textContent = `×${quantity}`;
          description.append(specification, count);
          row.append(term, description);
          return row;
        }));
      }

      if (productList) {
        const templates = this.equipmentCardTemplates || [];
        const cards = templates.length > 0 ? rows.map((item, index) => {
          const card = templates[index % templates.length].cloneNode(true);
          const media = card.querySelector('.savings-calculator-result__product-media');
          const name = card.querySelector('[data-equipment-name]');
          const quantity = card.querySelector('[data-equipment-quantity]');

          card.hidden = false;
          if (media) {
            media.querySelectorAll('img, .savings-calculator-result__product-placeholder').forEach((element) => element.remove());

            if (item.imageUrl) {
              const image = document.createElement('img');
              image.src = item.imageUrl;
              image.alt = item.productTitle;
              image.loading = 'lazy';
              media.insertBefore(image, quantity || null);
            } else {
              const placeholder = document.createElement('span');
              placeholder.className = 'savings-calculator-result__product-placeholder';
              placeholder.setAttribute('aria-hidden', 'true');
              media.insertBefore(placeholder, quantity || null);
            }
          }
          if (name) name.textContent = item.productTitle;
          if (quantity) quantity.textContent = `×${item.quantity}`;
          return card;
        }) : [];

        productList.replaceChildren(...cards);
      }

      this.scheduleCarouselSync(true);
      this.scheduleOverflowTooltipSync();
    }

    scheduleOverflowTooltipSync() {
      window.cancelAnimationFrame(this.overflowTooltipFrame);
      this.overflowTooltipFrame = window.requestAnimationFrame(() => {
        this.overflowTooltipFrame = null;
        this.syncOverflowTooltips();
      });
    }

    syncOverflowTooltips() {
      this.querySelectorAll('[data-overflow-tooltip]').forEach((element) => {
        this.syncOverflowTooltipTarget(element);
      });

      if (this.activeOverflowTooltipTarget && !this.activeOverflowTooltipTarget.hasAttribute('data-overflow-tooltip-active')) {
        this.hideOverflowTooltip();
      }
    }

    syncOverflowTooltipTarget(element) {
      const isOverflowing = element.scrollWidth > element.clientWidth + 1;
      element.toggleAttribute('data-overflow-tooltip-active', isOverflowing);
      element.removeAttribute('title');

      if (element.hasAttribute('data-overflow-tooltip-focusable')) {
        if (isOverflowing) {
          element.tabIndex = 0;
        } else {
          element.removeAttribute('tabindex');
        }
      }

      return isOverflowing;
    }

    handleOverflowTooltipEnter(event) {
      const target = event.target.closest('[data-overflow-tooltip]');

      if (
        !target
        || !this.contains(target)
        || event.pointerType === 'touch'
        || !this.syncOverflowTooltipTarget(target)
      ) {
        return;
      }

      this.showOverflowTooltip(target);
    }

    handleOverflowTooltipLeave(event) {
      const target = event.target.closest('[data-overflow-tooltip-active]');

      if (!target || target.contains(event.relatedTarget)) {
        return;
      }

      this.hideOverflowTooltip();
    }

    handleOverflowTooltipFocus(event) {
      const target = event.target.closest('[data-overflow-tooltip][data-overflow-tooltip-focusable]');

      if (target && this.contains(target) && this.syncOverflowTooltipTarget(target)) {
        this.showOverflowTooltip(target);
      }
    }

    handleOverflowTooltipBlur(event) {
      if (event.target.closest('[data-overflow-tooltip-focusable]')) {
        this.hideOverflowTooltip();
      }
    }

    showOverflowTooltip(target) {
      if (!this.overflowTooltip) {
        return;
      }

      const text = target.textContent.trim();
      if (!text) {
        return;
      }

      this.activeOverflowTooltipTarget = target;
      this.overflowTooltip.textContent = text;
      this.overflowTooltip.hidden = false;

      const targetRect = target.getBoundingClientRect();
      const tooltipRect = this.overflowTooltip.getBoundingClientRect();
      const viewportMargin = 12;
      const gap = 8;
      const centeredLeft = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
      const left = Math.min(
        window.innerWidth - tooltipRect.width - viewportMargin,
        Math.max(viewportMargin, centeredLeft)
      );
      const preferredTop = targetRect.top - tooltipRect.height - gap;
      const top = preferredTop >= viewportMargin ? preferredTop : targetRect.bottom + gap;

      this.overflowTooltip.style.left = `${left}px`;
      this.overflowTooltip.style.top = `${top}px`;
    }

    hideOverflowTooltip() {
      this.activeOverflowTooltipTarget = null;

      if (this.overflowTooltip) {
        this.overflowTooltip.hidden = true;
      }
    }

    updateCartActions() {
      if (!this.addToCartButton) {
        return;
      }

      const hasEquipment = this.resolvedEquipment.length > 0;
      const hasUnavailableItem = this.resolvedEquipment.some((item) => !item.available);
      this.addToCartButton.disabled = this.cartSubmitting || !hasEquipment || hasUnavailableItem;
      this.addToCartButton.setAttribute('aria-busy', this.cartSubmitting ? 'true' : 'false');

      if (hasEquipment && hasUnavailableItem) {
        this.showCartStatus(this.getString('cartUnavailable'));
      }
    }

    updateSetupDetailsLink() {
      if (!this.setupDetailsLink) {
        return;
      }

      const bundle = this.resolvedEquipment.find((item) => item.isBundle);
      const targetProduct = bundle || this.resolvedEquipment.find((item) => !item.isBundle);
      this.setupDetailsLink.href = targetProduct
        ? `${window.Shopify.routes.root}products/${encodeURIComponent(targetProduct.handle)}?variant=${encodeURIComponent(targetProduct.variantId)}`
        : window.Shopify.routes.root;
    }

    getCartItems() {
      const itemsByVariant = new Map();

      this.resolvedEquipment.forEach(({ variantId, quantity }) => {
        itemsByVariant.set(variantId, (itemsByVariant.get(variantId) || 0) + quantity);
      });

      return Array.from(itemsByVariant, ([id, quantity]) => ({ id, quantity }));
    }

    async addSetupToCart() {
      if (this.cartSubmitting || this.resolvedEquipment.length === 0) {
        return;
      }

      if (this.resolvedEquipment.some((item) => !item.available)) {
        this.showCartStatus(this.getString('cartUnavailable'));
        return;
      }

      this.cartSubmitting = true;
      this.hideCartStatus();
      this.updateCartActions();

      try {
        const sectionsToBundle = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: this.getCartItems(),
            sections: sectionsToBundle.join(','),
            sections_url: window.location.pathname
          })
        });
        const responseJson = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(responseJson?.description || responseJson?.message || this.getString('cartError'));
        }

        const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
          headers: { Accept: 'application/json' }
        });
        const cart = await cartResponse.json().catch(() => null);

        if (!cartResponse.ok || !cart) {
          throw new Error(this.getString('cartError'));
        }

        cart.sections = responseJson?.sections || {};
        const calculatorDialog = this.closest('savings-calculator-dialog');

        if (calculatorDialog?.open) {
          await calculatorDialog.hide();
        }

        this.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: Array.isArray(responseJson?.items) ? responseJson.items : [],
            cart,
            blockCartDrawerOpening: true
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart
          }
        }));

        await new Promise((resolve) => window.setTimeout(resolve, 0));

        const cartDrawerTrigger = document.querySelector('a[aria-controls="cart-drawer"]');
        if (cartDrawerTrigger) {
          cartDrawerTrigger.click();
        } else {
          window.location.href = `${window.Shopify.routes.root}cart`;
        }
      } catch (error) {
        console.error('[SigenMate calculator] Add setup to cart failed', error);
        this.showToast(error.message || this.getString('cartError'));
      } finally {
        this.cartSubmitting = false;
        this.updateCartActions();
      }
    }

    showCartStatus(message) {
      if (!this.cartStatus || !message) {
        return;
      }

      this.cartStatus.textContent = message;
      this.cartStatus.hidden = false;
    }

    hideCartStatus() {
      if (this.cartStatus) {
        this.cartStatus.hidden = true;
        this.cartStatus.textContent = '';
      }
    }

    formatCurrency(value, maximumFractionDigits = 0) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return '—';
      }

      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits
      }).format(number);
    }

    formatNumber(value, maximumFractionDigits = 0) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return '—';
      }

      return number.toLocaleString(document.documentElement.lang || undefined, { maximumFractionDigits });
    }

    renderResultAnswers() {
      const target = this.querySelector('[data-result-answers]');
      if (!target) {
        return;
      }

      const country = this.getSelected('country_code')?.dataset.display || '';
      const postcode = this.querySelector('[name="postcode"]')?.value.trim() || '';
      const inputMode = this.querySelector('[name="input_mode"]')?.value || 'annual_usage';
      const consumption = inputMode === 'monthly_bill'
        ? `${this.formatCurrency(this.querySelector('[name="monthly_bill_eur"]')?.value, 2)} / ${this.getString('month')}`
        : `${this.formatNumber(this.querySelector('[name="annual_usage_kwh"]')?.value)} kWh/${this.getString('year')}`;
      const tariff = this.getSelected('type')?.dataset.display || '';
      const solar = this.getSelected('has_existing_solar')?.dataset.display || '';
      const priority = this.getSelected('objective')?.dataset.display || '';
      const values = [
        { key: 'location', value: `${country} · ${postcode}` },
        { key: 'consumption', value: consumption },
        { key: 'tariff', value: tariff },
        { key: 'solar', value: solar },
        { key: 'priority', value: priority }
      ];

      target.replaceChildren(...values.map(({ key, value }) => {
        const item = document.createElement('span');
        item.className = 'savings-calculator-result__answer';
        item.dataset.answer = key;

        if (this.dataset.displayMode === 'dialog') {
          item.dataset.overflowTooltip = '';
          item.dataset.overflowTooltipFocusable = '';
        }

        item.textContent = value;
        return item;
      }));

      this.scheduleOverflowTooltipSync();
    }

    getString(key) {
      return this.querySelector('template[data-i18n]')?.content.querySelector(`[data-key="${key}"]`)?.textContent.trim() || '';
    }

    showToast(message) {
      if (!this.toast || !message) {
        return;
      }

      window.clearTimeout(this.toastTimer);
      this.toast.textContent = message;
      this.toast.hidden = false;
      this.toastTimer = window.setTimeout(() => this.hideToast(), 3200);
    }

    hideToast() {
      if (this.toast) {
        this.toast.hidden = true;
        this.toast.textContent = '';
      }
    }
  }

  window.customElements.define('savings-calculator', SavingsCalculator);
}

if (!window.customElements.get('savings-calculator-dialog')) {
  class SavingsCalculatorDialog extends DialogElement {
    connectedCallback() {
      super.connectedCallback();

      if (this.savingsDialogAbortController) {
        return;
      }

      this.savingsDialogAbortController = new AbortController();
      const signal = this.savingsDialogAbortController.signal;
      this.calculator = this.querySelector('savings-calculator');

      this.querySelector('[data-dialog-dismiss]')?.addEventListener('click', () => this.hide(), { signal });
      this.addEventListener('dialog:after-hide', () => this.calculator?.resetCalculator(), { signal });
      this.addEventListener('dialog:after-show', () => this.calculator?.updateContinueState(), { signal });
    }

    disconnectedCallback() {
      this.savingsDialogAbortController?.abort();
      this.savingsDialogAbortController = null;
      super.disconnectedCallback();
    }

    get shouldLock() {
      return true;
    }

    _showTransition() {
      return Promise.resolve();
    }

    _hideTransition() {
      return Promise.resolve();
    }
  }

  window.customElements.define('savings-calculator-dialog', SavingsCalculatorDialog);
}
